import { postJson } from 'application/utils/csrf-request'
import type {
  CreateBookingData,
  BookingResponse,
  InitPaymentData,
  CertificateCheckResponse,
  ValidatePromocodeData,
  PromocodeValidationResponse
} from './types'

/**
 * Класс для работы с API бронирования
 */
export class BookingAPI {
  /**
   * Создать бронирование
   * @param {CreateBookingData} data - данные для создания бронирования
   * @return {Promise<BookingResponse>} ответ с ID бронирования
   */
  async createBooking(data: CreateBookingData): Promise<BookingResponse> {
    const { response, data: responseData } = await postJson<
      BookingResponse | BookingResponse[]
    >('/api/booking', data)

    // eslint-disable-next-line no-console
    console.debug('Booking response:', responseData)

    // Данные приходят массивом, берем первый элемент
    const bookingData: BookingResponse = Array.isArray(responseData)
      ? responseData[0]
      : responseData

    if (
      bookingData.validate_phone ||
      bookingData.success === false ||
      bookingData.result === false
    ) {
      return bookingData
    }

    if (!response.ok) {
      throw new Error(
        bookingData.error_text ||
          bookingData.user_msg_display ||
          bookingData.error ||
          'Ошибка создания бронирования'
      )
    }

    if (!bookingData.id && !bookingData.data?.id) {
      throw new Error('Ошибка создания бронирования')
    }

    return bookingData
  }

  /**
   * Проверить промокод: доступен ли он для выбранного квеста/таймслота/клиента,
   * и какая скидка у него настроена (сумма скидки считается на клиенте от текущей
   * стоимости бронирования, окончательная и обязательная проверка — на сервере,
   * при создании бронирования).
   * @param {ValidatePromocodeData} data - квест, таймслот и контакты клиента
   */
  async validatePromoCode(
    data: ValidatePromocodeData
  ): Promise<PromocodeValidationResponse> {
    const { response, data: responseData } =
      await postJson<PromocodeValidationResponse>(
        '/api/booking/promo-code',
        data
      )

    if (!response.ok || responseData.success === false) {
      throw new Error(responseData.error_text || 'Промокод недействителен')
    }

    return responseData
  }

  /**
   * Проверить подарочный сертификат до бронирования: номер + код → номинал.
   * Списание делает сервер при создании брони (certificate_number/certificate_code
   * в createBooking), поэтому здесь ничего не тратится.
   * @param {string} number - номер сертификата
   * @param {string} code - код сертификата
   * @param {string} currency - валюта брони: номинал в другой валюте сервер
   * вернёт пересчитанным по курсу ЦБ РФ
   */
  async checkCertificate(
    number: string,
    code: string,
    currency: string
  ): Promise<CertificateCheckResponse> {
    const { response, data } = await postJson<CertificateCheckResponse>(
      '/api/certificates/check',
      {
        // eslint-disable-next-line camelcase
        certificate_number: number,
        // eslint-disable-next-line camelcase
        certificate_code: code,
        currency
      }
    )

    if (!response.ok || data.status !== 'success') {
      throw new Error(
        (data.status === 'notfound' ? data.title : data.text || data.title) ||
          'Сертификат неверный'
      )
    }

    return data
  }

  /**
   * Инициировать оплату
   * @param {string} gameId - ID бронирования
   * @param {InitPaymentData} data - данные платежа
   * @return {Promise<string | null>} URL для редиректа в платежный сервис или null, если оплата не требуется
   */
  async initPayment(
    gameId: string,
    data: InitPaymentData
  ): Promise<string | null> {
    const { response, data: responseData } = await postJson<
      BookingResponse & { payment_url?: string }
    >(`/api/booking/${gameId}/pay`, data)

    if (
      !response.ok ||
      responseData.success === false ||
      responseData.result === false
    ) {
      // Сервер объясняет отказ (нет слота, нужна предоплата, способ
      // недоступен) — без этого клиент видел безликую ошибку и жал снова.
      throw new Error(
        responseData.error_text ||
          responseData.error ||
          'Ошибка инициализации оплаты'
      )
    }

    return responseData.payment_url || null
  }
}
